import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useStore } from "@/contexts/StoreContext";
import QtyStepper from "@/components/customer/QtyStepper";
import ProductCard from "@/components/customer/ProductCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Trash2, ShoppingCart, ArrowRight, ShoppingBag, Lock, AlertTriangle,
  Package, Ticket, Truck, PiggyBank, Timer, CheckCircle2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { cart, loading, refresh, remove } = useCart();
  const { canOrder, isOpen, isServiceable, closedReason, deliveryFee, freeOver, minOrder, etaMinutes } = useStore();
  const nav = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [recommended, setRecommended] = useState([]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load a small recommendations rail (reuses public products list).
  useEffect(() => {
    let cancel = false;
    api.get("/products?limit=12")
      .then((r) => { if (!cancel) setRecommended((r.data.items || []).filter((p) => !/^TEST[_-]/i.test(p.name || ""))); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const items = cart?.items || [];
  const totals = cart?.totals || {};
  const subtotal = Number(totals.subtotal || 0);
  const tax = Number(totals.tax || 0);
  const discount = Number(totals.discount || 0);
  const total = Number(totals.total_before_delivery || (subtotal - discount));

  // Savings — sum of (mrp - selling) × qty. Falls back to zero if line has no mrp.
  const savings = items.reduce((s, it) => {
    const mrp = Number(it.mrp || 0);
    const price = Number(it.price_snapshot || it.selling_price || 0);
    if (!mrp || mrp <= price) return s;
    return s + (mrp - price) * Number(it.qty || 0);
  }, 0);

  // Delivery
  const effectiveFee = subtotal >= Number(freeOver || 0) ? 0 : Number(deliveryFee || 0);
  const grand = total + effectiveFee;
  const belowMin = Number(minOrder || 0) > 0 && subtotal < Number(minOrder);
  const shortBy = belowMin ? Number(minOrder) - subtotal : 0;
  const remainingForFreeDelivery = freeOver && subtotal < Number(freeOver) ? Number(freeOver) - subtotal : 0;

  if (loading && items.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-3" data-testid="cart-loading">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}
      </div>
    );
  }

  if (items.length === 0) {
    // Filter recommendations from any potential TEST_ names, get first 6
    const empty_recos = recommended.slice(0, 6);
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14" data-testid="cart-empty">
        <div className="text-center">
          <div className="relative w-24 h-24 rounded-2xl bg-primary/10 text-primary grid place-items-center mx-auto mb-5">
            <ShoppingCart className="w-11 h-11" />
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold grid place-items-center">0</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Your cart is empty</h1>
          <p className="mt-2 text-muted-foreground text-sm md:text-base">Fresh from Babu Store is just a few taps away.</p>
          <Button asChild size="lg" className="mt-6 btn-primary-glow" data-testid="empty-cart-shop-btn">
            <Link to="/shop"><ShoppingBag className="w-4 h-4 mr-2" />Browse products</Link>
          </Button>
        </div>

        {empty_recos.length > 0 && (
          <section className="mt-12" data-testid="cart-empty-recos">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-lg md:text-xl font-display font-semibold">Popular right now</h2>
              <Link to="/shop" className="text-xs text-primary hover:underline font-semibold">See all →</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4">
              {empty_recos.map((p) => (<ProductCard key={p.id} p={p} testId={`empty-reco-${p.id}`} />))}
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-6 py-4 md:py-6 pb-32 md:pb-8" data-testid="cart-page">
      <div className="mb-3 md:mb-5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
          <ShoppingCart className="w-4 h-4" />
        </div>
        <div>
          <h1 className="text-xl md:text-3xl font-display font-semibold leading-tight">Your cart</h1>
          <p className="text-[11px] md:text-xs text-muted-foreground">
            {items.length} item{items.length !== 1 ? "s" : ""} · delivers in {etaMinutes || 15} min
          </p>
        </div>
      </div>

      {/* Delivery progress hint — celebrates when unlocked */}
      {Number(freeOver || 0) > 0 && (
        <div
          className={`mb-3 rounded-xl border p-3 flex items-center gap-3 text-sm transition ${
            remainingForFreeDelivery > 0 ? "bg-card border-border" : "bg-emerald-50 border-emerald-200 text-emerald-900"
          }`}
          data-testid="free-delivery-hint"
        >
          <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${remainingForFreeDelivery > 0 ? "bg-primary/10 text-primary" : "bg-emerald-100 text-emerald-700"}`}>
            {remainingForFreeDelivery > 0 ? <Truck className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            {remainingForFreeDelivery > 0 ? (
              <>
                Add <span className="font-bold tabular-nums">{money(remainingForFreeDelivery)}</span> more for <span className="font-bold text-primary">FREE delivery</span>
              </>
            ) : (
              <span className="font-semibold">You unlocked FREE delivery on this order!</span>
            )}
            <div className="mt-1.5 h-2 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${remainingForFreeDelivery > 0 ? "bg-gradient-to-r from-primary/70 to-primary" : "bg-emerald-500"}`}
                style={{ width: `${Math.min(100, (subtotal / Number(freeOver)) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}
      {savings > 0 && (
        <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3 text-sm text-emerald-900" data-testid="cart-savings-strip">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
            <PiggyBank className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">You&apos;re saving <span className="tabular-nums">{money(savings)}</span> on this order</div>
            <div className="text-[11px] text-emerald-800/80">Bagged from live discounts across Babu Store</div>
          </div>
          <Sparkles className="w-4 h-4 text-emerald-600" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 space-y-2">
          {items.map((it) => {
            const lineMrp = Number(it.mrp || 0);
            const linePrice = Number(it.price_snapshot || it.selling_price || 0);
            const lineDiscount = lineMrp > linePrice ? Math.round(((lineMrp - linePrice) / lineMrp) * 100) : 0;
            const lineTotal = linePrice * Number(it.qty || 0);
            return (
              <Card key={it.product_id} className="border-border/60 rounded-2xl shadow-none overflow-hidden fade-in-up hover:border-border transition-colors" data-testid={`cart-line-${it.product_id}`}>
                <CardContent className="p-2.5 md:p-3 flex items-center gap-2 md:gap-3">
                  <Link to={`/product/${it.product_id}`} className="w-14 h-14 md:w-20 md:h-20 rounded-xl bg-white border border-border/60 overflow-hidden shrink-0 grid place-items-center p-1">
                    {it.thumbnail_url
                      ? <img src={it.thumbnail_url} alt="" className="w-full h-full object-contain" loading="lazy" />
                      : <Package className="w-5 h-5 text-primary/40" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${it.product_id}`} className="text-[13px] md:text-sm font-semibold hover:text-primary line-clamp-2 leading-tight">{it.name}</Link>
                    {it.weight && <div className="text-[11px] text-muted-foreground mt-0.5">{it.weight} {it.weight_unit || "g"}</div>}
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-bold text-foreground tabular-nums">{money(linePrice)}</span>
                      {lineMrp > linePrice && (
                        <>
                          <span className="text-[11px] text-muted-foreground line-through tabular-nums">{money(lineMrp)}</span>
                          <span className="text-[10px] font-mono font-bold text-emerald-700">-{lineDiscount}%</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0 min-w-0">
                    <QtyStepper product={{ id: it.product_id, name: it.name }} size="sm" testIdPrefix="cart" />
                    <div className="text-[13px] font-semibold tabular-nums whitespace-nowrap" data-testid={`cart-line-total-${it.product_id}`}>{money(lineTotal)}</div>
                  </div>
                  <button
                    onClick={() => remove(it.product_id)}
                    className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition shrink-0 press"
                    data-testid={`cart-remove-${it.product_id}`}
                    aria-label="Remove"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div>
          <Card className="border-border/60 rounded-2xl shadow-none lg:sticky lg:top-24" data-testid="cart-summary">
            <CardContent className="p-4 space-y-3">
              <div className="font-display text-lg font-semibold">Bill details</div>

              {/* Coupon */}
              <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/[0.03] p-2 flex items-center gap-2" data-testid="coupon-placeholder">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><Ticket className="w-4 h-4" /></div>
                <Input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder="Have a coupon?"
                  className="h-8 border-transparent bg-transparent focus-visible:ring-0 text-sm font-semibold tracking-wider placeholder:font-normal placeholder:tracking-normal"
                  data-testid="coupon-input"
                />
                <Button size="sm" className="h-8 btn-primary-glow" disabled={!coupon} onClick={() => toast.info("Coupons launching soon")} data-testid="coupon-apply">Apply</Button>
              </div>

              <div className="pt-2 space-y-1.5 text-[13px] border-t">
                <div className="flex justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Item total</span>
                  <span className="tabular-nums" data-testid="cart-sub">{money(subtotal)}</span>
                </div>
                {tax > 0 && (
                  <div className="flex justify-between text-[11px] text-muted-foreground">
                    <span>Inclusive of taxes</span>
                    <span className="tabular-nums">{money(tax)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span className="inline-flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5" />Coupon discount</span>
                    <span className="tabular-nums font-semibold">− {money(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" />Delivery fee</span>
                  <span className="tabular-nums" data-testid="cart-delivery-fee">{effectiveFee === 0 ? <span className="text-emerald-700 font-semibold">FREE</span> : money(effectiveFee)}</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span className="inline-flex items-center gap-1.5"><PiggyBank className="w-3.5 h-3.5" />You save</span>
                    <span className="tabular-nums font-semibold">{money(savings)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg md:text-xl font-display font-semibold pt-2 mt-2 border-t">
                  <span>To pay</span><span className="tabular-nums" data-testid="cart-total">{money(grand)}</span>
                </div>
              </div>

              {belowMin && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-50 text-amber-900 p-2.5 text-xs flex items-start gap-2" data-testid="min-order-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>Add <span className="font-semibold">{money(shortBy)}</span> more to place an order. Minimum order is {money(minOrder)}.</div>
                </div>
              )}

              {!canOrder && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-2.5 flex items-start gap-2 text-sm" data-testid="cart-order-blocked">
                  {!isOpen ? <Lock className="w-4 h-4 mt-0.5 text-destructive shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive shrink-0" />}
                  <div>
                    <div className="font-semibold text-destructive text-sm">
                      {!isOpen ? "Store is currently closed" : "Delivery unavailable at your location"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {!isOpen ? (closedReason || "Please try again during operating hours.") : "Change your pincode from the header to check nearby zones."}
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop-only checkout button (mobile has sticky bar below) */}
              {canOrder && !belowMin ? (
                <Button asChild size="lg" className="w-full btn-primary-glow hidden lg:flex" data-testid="cart-checkout-btn">
                  <Link to="/checkout">
                    <span className="flex-1 text-left">Checkout · {money(grand)}</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              ) : (
                <Button size="lg" disabled className="w-full hidden lg:flex" data-testid="cart-checkout-disabled">
                  <Lock className="w-4 h-4 mr-2" /> {!isOpen ? "Store closed" : belowMin ? `Add ${money(shortBy)} more` : "Unavailable at your location"}
                </Button>
              )}

              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground justify-center pt-1">
                <Timer className="w-3 h-3" /> Delivers in ~{etaMinutes || 15} min from Babu Store
              </div>

              <Button asChild size="sm" variant="ghost" className="w-full text-muted-foreground">
                <Link to="/shop">← Continue shopping</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recommendations rail — "You may also like" */}
      {recommended.length > 0 && (
        <section className="mt-8 md:mt-10" data-testid="cart-recos">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-lg md:text-xl font-display font-semibold">You may also like</h2>
            <Link to="/shop" className="text-xs text-primary hover:underline font-semibold">See all →</Link>
          </div>
          <div className="snap-rail rail-fade-mask md:grid md:grid-cols-6 md:gap-4">
            {recommended
              .filter((p) => !items.some((it) => it.product_id === p.id))
              .slice(0, 8)
              .map((p) => (
                <div key={p.id} className="snap-start shrink-0 w-[44vw] max-w-[180px] md:w-auto md:max-w-none" data-testid={`cart-reco-${p.id}`}>
                  <ProductCard p={p} />
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Sticky mobile checkout bar — Blinkit style, sits above bottom-nav */}
      <div className="lg:hidden fixed left-0 right-0 bottom-16 z-30 bg-card border-t shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.12)] px-3 py-2.5 slide-up-in" data-testid="cart-mobile-cta">
        {canOrder && !belowMin ? (
          <Button asChild size="lg" className="w-full h-12 rounded-xl btn-primary-glow justify-between" data-testid="cart-checkout-btn-mobile">
            <Link to="/checkout">
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-wider opacity-90">{items.length} item{items.length !== 1 ? "s" : ""} · Total</span>
                <span className="text-base font-bold tabular-nums">{money(grand)}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold">Checkout <ArrowRight className="w-4 h-4" /></span>
            </Link>
          </Button>
        ) : (
          <Button size="lg" disabled className="w-full h-12 rounded-xl" data-testid="cart-checkout-disabled-mobile">
            <Lock className="w-4 h-4 mr-2" /> {!isOpen ? "Store closed" : belowMin ? `Add ${money(shortBy)} more` : "Unavailable"}
          </Button>
        )}
      </div>
    </div>
  );
}
